<template>
  <BaseModal
    v-if="!isFetching"
    color="grey"
    drop-shadow
    rounded
    class="edit-user-modal"
  >
    <h1 class="edit-user-modal__heading">Edit "{{ user.name }}"</h1>
    <form @submit.prevent="submitUpdate" class="edit-user-modal__form">
      <FormSelect
        v-model="form.data.subscriptionPermissions"
        id="edit-user-modal__subscription-permissions"
        label-text="Subscription Permissions:"
        :options="[
          {
            value: -1,
            label: 'None',
          },
          {
            value: authUsersTrendReport ? authUsersTrendReport.id : 0,
            label: 'Trends Report',
          },
        ]"
        :disabled="!authUsersTrendReport"
        :invalid="!!form.errors.subscriptionPermissions"
        row
        width-split="1/3"
        no-error
      />
      <FormSelect
        v-model="form.data.reportAccess"
        id="edit-user-modal__report-access"
        label-text="Report Access:"
        :options="reportTypeOptions"
        placeholder-text="Select answer(s)"
        multiselect
        hasSelectAll
        :invalid="!!form.errors.reportAccess"
        row
        width-split="1/3"
        no-error
      />
      <FormSelect
        v-model="form.data.dataAccess"
        id="edit-user-modal__data-access"
        label-text="Data Access:"
        :options="dataAccessOptions"
        placeholder-text="Select answer"
        :invalid="!!form.errors.dataAccess"
        row
        width-split="1/3"
        no-error
      />
      <FormSelect
        v-model="form.data.primaryPOCReports"
        id="edit-user-modal__primary-poc-reports"
        label-text="Primary Point of Contact for Report(s):"
        :options="validContactReportTypes"
        placeholder-text="Select answer(s)"
        multiselect
        hasSelectAll
        :invalid="!!form.errors.primaryPOCReports"
        row
        width-split="1/3"
        no-error
      />
      <FormSelect
        v-model="form.data.primaryDataReports"
        id="edit-user-modal__primary-data-reports"
        label-text="Primary Data Submissions Contact for Report(s):"
        :options="validContactReportTypes"
        placeholder-text="Select answer(s)"
        multiselect
        hasSelectAll
        :invalid="!!form.errors.primaryDataReports"
        row
        width-split="1/3"
        no-error
      />
      <div class="edit-user-modal__group">
        <BaseSwitch v-model="form.data.companyAdmin" center>
          <template #before>Assign admin?</template>
        </BaseSwitch>
        <BaseSwitch v-model="form.data.hasNotifications" center>
          <template #before>Receive report notifications?</template>
        </BaseSwitch>
      </div>
      <div class="edit-user-modal__group">
        <BaseButton type="submit" size="sm" class="edit-user-modal__button">
          UPDATE
        </BaseButton>
        <BaseButton
          type="button"
          size="sm"
          color="charcoal"
          class="edit-user-modal__button"
          @click="$emit('close')"
        >
          CANCEL
        </BaseButton>
      </div>
    </form>
  </BaseModal>
</template>

<script>
  import BaseModal from '~/components/base/BaseModal.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseSwitch from '~/components/base/BaseSwitch.vue';
  import FormSelect from '~/components/form/FormSelect.vue';

  export default {
    name: 'EditUserModal',
    components: {
      BaseModal,
      BaseButton,
      BaseSwitch,
      FormSelect,
    },
    props: {
      user: {
        type: Object,
        required: true,
      },
      authUser: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      isUpdating: false,
      userRole: false,
      userNotifications: false,
      form: {
        data: {
          subscriptionPermissions: 0,
          reportAccess: [],
          dataAccess: '',
          primaryPOCReports: [],
          primaryDataReports: [],
          companyAdmin: false,
          hasNotifications: false,
        },
        errors: {
          subscriptionPermissions: '',
          reportAccess: '',
          dataAccess: '',
          primaryPOCReports: '',
          primaryDataReports: '',
        },
      },
      reportTypeOptions: [],
      authUsersTrendReport: null,
      isFetching: false,
      dataAccessOptions: [
        {
          value: 'none',
          label: 'No access',
        },
        {
          value: 'read-only',
          label: 'Read-only access',
        },
        {
          value: 'full',
          label: 'Full access',
        },
      ],
    }),
    computed: {
      validContactReportTypes() {
        // THIS IS NECCESSARY BECAUSE WE NEED TO FILTER OUT REPORT TYPES THAT THE USER DOES NOT HAVE ACCESS TO
        // IF THEY ARE BEING GIVEN ACCESS TO A REPORT TYPE, THEY SHOULD BE ABLE TO BE SELECTED AS A CONTACT FOR THAT REPORT TYPE
        return this.reportTypeOptions.filter((reportTypeOption) =>
          this.form.data.reportAccess.find(
            (reportType) => reportType === reportTypeOption.value
          )
        );
      },
    },
    async created() {
      try {
        this.isFetching = true;
        const { data } = await axios.get('/company-admin/report/list');
        this.reportTypeOptions = data;

        this.form.data.dataAccess = this.user.data_access;
        this.form.data.companyAdmin = this.user.company_administrator;
        this.form.data.hasNotifications = this.user.has_notifications;
        this.form.data.reportAccess = this.user.consulting_report_types.map(
          (reportType) => reportType.id
        );

        const usersTrendReportSubscription =
          this.user.active_subscriptions.find(
            (subscription) => subscription.product.name === 'Trends Report'
          );
        this.form.data.subscriptionPermissions =
          usersTrendReportSubscription && usersTrendReportSubscription.product
            ? usersTrendReportSubscription.product.id
            : 0;

        const adminTrendReportSubscription =
          this.authUser.active_subscriptions.find(
            (subscription) => subscription.product.name === 'Trends Report'
          );
        this.authUsersTrendReport = adminTrendReportSubscription
          ? adminTrendReportSubscription.product
          : null;

        this.form.data.primaryPOCReports = this.user.report_type_contacts.map(
          (reportType) => reportType.id
        );
        this.form.data.primaryDataReports =
          this.user.report_type_data_contacts.map(
            (reportType) => reportType.id
          );
      } catch (error) {
        // TODO: HANDLE ERROR - This is just silencing the error
      } finally {
        this.isFetching = false;
      }
    },
    methods: {
      async submitUpdate() {
        if (this.isUpdating) return;

        try {
          this.isUpdating = true;
          await axios.put(`/company-admin/user/${this.user.id}`, {
            subscription_access: this.form.data.subscriptionPermissions,
            report_access: this.form.data.reportAccess,
            data_access: this.form.data.dataAccess,
            poc_reports: this.form.data.primaryPOCReports,
            data_contact_reports: this.form.data.primaryDataReports,
            company_administrator: this.form.data.companyAdmin,
            has_notifications: this.form.data.hasNotifications,
          });
          this.$emit('close');
        } catch (error) {
          // TODO: HANDLE ERROR - This is just silencing the error
        } finally {
          this.isUpdating = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .edit-user-modal {
    &__heading {
      margin: 0;
      margin-bottom: 40px;
      text-align: center;
      width: 100%;
    }
    &__form {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      gap: 16px;
      width: 100%;
    }
    &__group {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: fit-content;
      gap: 32px;
      margin: 8px 0;
    }
    &__button {
      width: 100%;
      min-width: 200px;
      font-size: 14px;
    }

    :deep(.base-switch) {
      gap: 16px;
    }

    @include screen-md {
      &__group {
        flex-direction: row;
      }
    }
  }
</style>
